/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { FC } from 'react';
import Button from '~/components/commonUi/Button/Button';
import Fieldset from '~/components/commonUi/Fieldset';
import SelectText, { SelectTextProps } from '~/components/commonUi/SelectText/SelectText';
import TextArea from '~/components/commonUi/TextArea/TextArea';
import {
    COMPANY_LABEL,
    ELECTION_FIELD_CONFIG,
    ELECTION_FIELD_DATA,
    electionFieldConfigKeys,
} from '~/consts/generalSettings';
import { Company } from '~/definitions/company';
import { Election } from '~/definitions/election';

interface Props {
    companyData: Company;
    electionList: Election[];
    isSettingsDisabled: boolean;
    isLoadingElectionList: boolean;
    preSelectElectionId: string;
    selectedElectionData: Election;
    slagDescription: string;
    slagHowToVote: string;
    handleChangeElectionAttribute: (key: string, value: string) => void;
    handleUpdateElection: () => Promise<() => void>;
    updateElectionData: () => void;
    setPreSelectElectionId: (value: string) => void;
    setSlagDescription: (value: string) => void;
    setSlagHowToVote: (value: string) => void;
}

const SlagSettingsDetails: FC<Props> = (props) => {
    const {
        companyData = {},
        electionList = [],
        isSettingsDisabled,
        isLoadingElectionList,
        preSelectElectionId,
        selectedElectionData,
        slagDescription,
        slagHowToVote,
        handleChangeElectionAttribute,
        handleUpdateElection,
        updateElectionData,
        setPreSelectElectionId,
        setSlagDescription,
        setSlagHowToVote,
    } = props;

    if (isLoadingElectionList) {
        return null;
    }

    const getOptions = (): SelectTextProps['options'] => {
        let options: SelectTextProps['options'] = [];
        options = electionList?.map((e) => ({
            value: e.id as string,
            label: e.name as string,
        }));
        return options;
    };

    const slag = companyData?.name || '';
    const electionName = selectedElectionData?.name || '';
    const title = `${slag} / ${electionName}`;
    const availableElection = getOptions();

    const rowClassName = 'grid grid-cols-12 mb-4 gap-4';
    const firstColClassName = 'col-span-3 text-left font-normal';
    const secondColClassName = 'col-span-9';

    const handleChangeElection = (value: string) => {
        setPreSelectElectionId(value);
    };

    const handleChangeDescription = (value: string) => {
        setSlagDescription(value);
        handleChangeElectionAttribute(ELECTION_FIELD_CONFIG.electionSLagDescription, value);
    };

    const handleChangeHowToVote = (value: string) => {
        setSlagHowToVote(value);
        handleChangeElectionAttribute(ELECTION_FIELD_CONFIG.electionHowToVoteDescription, value);
    };

    const handleSaveChanges = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        updateElectionData();
    };

    const renderElectionSettingFields = (): JSX.Element[] => {
        const fields: JSX.Element[] = [];

        electionFieldConfigKeys.forEach((key) => {
            const field = ELECTION_FIELD_DATA[key];
            const fieldId = field.id;

            if (key === ELECTION_FIELD_CONFIG.electionSLagDescription) {
                fields.push(
                    <div className={rowClassName} key={key}>
                        <label htmlFor={fieldId} className={firstColClassName}>
                            {field.label}
                        </label>
                        <div className={secondColClassName}>
                            <TextArea
                                disabled={isSettingsDisabled}
                                extendedClassName="h-[120px] w-full"
                                id={fieldId}
                                key={fieldId}
                                name={field.name}
                                onChange={(event) => handleChangeDescription(event.target.value)}
                                testId={fieldId}
                                value={slagDescription}
                            />
                        </div>
                    </div>
                );
            } else if (key === ELECTION_FIELD_CONFIG.electionHowToVoteDescription) {
                fields.push(
                    <div className={rowClassName} key={key}>
                        <label htmlFor={fieldId} className={firstColClassName}>
                            {field.label}
                        </label>
                        <div className={secondColClassName}>
                            <TextArea
                                disabled={isSettingsDisabled}
                                extendedClassName="h-[120px] w-full"
                                id={fieldId}
                                key={fieldId}
                                name={field.name}
                                onChange={(event) => handleChangeHowToVote(event.target.value)}
                                testId={fieldId}
                                value={slagHowToVote}
                            />
                        </div>
                    </div>
                );
            }
        });

        return fields;
    };

    const renderElectionSelection = () => {
        return (
            <>
                <div className="mb-4">
                    <SelectText
                        name={'settings_election_list'}
                        onChange={(event) => handleChangeElection(event.target.value)}
                        value={preSelectElectionId}
                        options={availableElection}
                        testId={'settings-election-list'}
                        extendedClassName={'w-full'}
                    />
                </div>
                <div className="mb-4">
                    <Button
                        btnText={'Bytt valgoppsett'}
                        testId={'settings-election-button'}
                        onClick={handleUpdateElection}
                    />
                </div>
            </>
        );
    };

    return (
        <Fieldset title={title + ` innstillinger`}>
            <form
                action="settings"
                method="post"
                className={'frm-settings'}
                data-test-id="frm-settings"
                onSubmit={handleSaveChanges}
            >
                <div className="flex flex-col lg:flex-row lg:gap-8 mb-4">
                    <div className="w-full lg:w-2/3">
                        {/* Left side: S-Lag content */}
                        <div className={rowClassName}>
                            <div className={firstColClassName}>{COMPANY_LABEL}</div>
                            <div className={secondColClassName}>{title}</div>
                        </div>
                        {renderElectionSettingFields()}
                    </div>
                    <div className="w-full lg:w-1/3">
                        {/* Right side: S-Lag content */}
                        {renderElectionSelection()}
                    </div>
                </div>
                <div className="text-right mb-[16px]">
                    {!isSettingsDisabled && <Button type={'submit'} btnText={'Lagre'} testId={'save-button'} />}
                </div>
            </form>
        </Fieldset>
    );
};

export default SlagSettingsDetails;
